import { useRef } from 'react'
import { Check, X } from 'lucide-react'
import { MarkdownEditor } from '@/components/MarkdownEditor'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { usePromptDetailContext } from '@/hooks'

const labelClass = 'text-xs font-bold leading-[160%] tracking-[0.015em] text-foreground'

export function PromptConfigPage() {
  const {
    canEdit,
    name,
    onNameChange,
    internalName,
    onInternalNameChange,
    description,
    onDescriptionChange,
    content,
    onContentChange,
    validationErrors,
    isDirty,
    isSaving,
    saveBlockedReason,
    onSave,
    onCancel,
  } = usePromptDetailContext()

  // The editor re-serialises the saved markdown into its own normalised form and can report that as
  // a change on load; only changes made after the user touches the editor count as edits.
  const editorTouchedRef = useRef(false)
  const markEditorTouched = () => {
    editorTouchedRef.current = true
  }
  const handleContentChange = (value: string) => {
    if (editorTouchedRef.current) onContentChange(value)
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-5 rounded-2xl border border-border bg-card p-5" data-testid="prompt-config-section">
      {canEdit && (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={!isDirty || isSaving} data-testid="cancel-prompt-button">
            <X className="h-4 w-4" />
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saveBlockedReason !== null}
            title={saveBlockedReason ?? undefined}
            data-testid="save-prompt-button"
          >
            <Check className="h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save Prompt'}
          </Button>
        </div>
      )}

      <div className="grid shrink-0 grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="prompt-name" className={labelClass}>
            Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="prompt-name"
            value={name}
            onChange={e => onNameChange(e.target.value)}
            disabled={!canEdit}
            className="h-10 rounded-lg"
            placeholder="Enter prompt name"
            data-testid="prompt-name-input"
          />
          {canEdit && validationErrors.name && (
            <p className="text-sm text-destructive" role="alert">{validationErrors.name}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="prompt-internal-name" className={labelClass}>
            Internal Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="prompt-internal-name"
            value={internalName}
            onChange={e => onInternalNameChange(e.target.value)}
            disabled={!canEdit}
            className="h-10 rounded-lg"
            placeholder="Enter internal name"
            data-testid="prompt-internal-name-input"
          />
          {canEdit && validationErrors.internalName && (
            <p className="text-sm text-destructive" role="alert">{validationErrors.internalName}</p>
          )}
        </div>
      </div>

      <div className="shrink-0 space-y-2">
        <Label htmlFor="prompt-description" className={labelClass}>Description</Label>
        <Textarea
          id="prompt-description"
          value={description}
          onChange={e => onDescriptionChange(e.target.value)}
          disabled={!canEdit}
          rows={2}
          placeholder="Enter prompt description"
          data-testid="prompt-description-input"
        />
        {canEdit && validationErrors.description && (
          <p className="text-sm text-destructive" role="alert">{validationErrors.description}</p>
        )}
      </div>

      <div
        className="flex min-h-0 flex-1 flex-col gap-2"
        onKeyDownCapture={markEditorTouched}
        onPointerDownCapture={markEditorTouched}
      >
        <Label htmlFor="prompt-content" className={labelClass}>
          Prompt Content <span className="text-red-500">*</span>
        </Label>
        <MarkdownEditor
          id="prompt-content"
          dataTestId="prompt-content-input"
          value={content}
          onChange={handleContentChange}
          placeholder="Enter prompt content"
          disabled={!canEdit}
          className="flex min-h-0 flex-1 flex-col [&>div:last-child]:flex-1 [&>div:last-child]:overflow-y-auto"
        />
        {canEdit && validationErrors.content && (
          <p className="text-sm text-destructive" role="alert">{validationErrors.content}</p>
        )}
      </div>
    </div>
  )
}
